import { useEffect, useRef } from "react";

/**
 * Hook that enables scroll-through behavior when an element doesn't need to scroll.
 * If the element has no scrollbar (content fits), wheel events are delegated to a parent element.
 *
 * We MAY want to move this hook up at some point so it can be used by more than just the TemplateVitePressDocs
 */
export function useScrollThrough<T extends HTMLElement = HTMLDivElement>(
	delegateToSelector = "main",
	baseMultiplier = 6
) {
	const elementRef = useRef<T>(null);

	useEffect(() => {
		//next ssr check
		if (typeof window === "undefined") return;

		const element = elementRef.current;
		if (!element) return;

		const handleWheel = (event: WheelEvent) => {
			const { scrollTop, scrollHeight, clientHeight } = element;
			const isAtTop = scrollTop === 0;
			const isAtBottom = scrollTop + clientHeight >= scrollHeight;
			const canScroll = scrollHeight > clientHeight;

			if (!canScroll || (isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
				const delegateElement = document.querySelector(delegateToSelector) as HTMLElement;

				if (delegateElement && delegateElement !== element) {
					event.preventDefault();

					// Apply the multiplier param to match natural scroll speed
					// In experimenting I found that 6 feels mostly natural,
					// but I believe we may run into edge cases where this does not feel right
					const deltaY = event.deltaY * baseMultiplier;
					const deltaX = event.deltaX * baseMultiplier;

					delegateElement.scrollBy({
						top: deltaY,
						left: deltaX,
						behavior: "smooth",
					});
				}
			}
		};

		// passive mode so that we can use preventDefault()
		element.addEventListener("wheel", handleWheel, { passive: false });

		return () => {
			element.removeEventListener("wheel", handleWheel);
		};
	}, [delegateToSelector, baseMultiplier]);

	return elementRef;
}
